import flatpickr from 'flatpickr';
import { normalizeWidgetTheme, APIURL, appendFlatpickrBaseStyles, cancelSelectEvent, getCSSStyles, getWidgetDataURI, apiGet, initFlatpickrInShadow, toggleWidgetTheme, isHeaderPinned, getHeaderPinStateClass, getHeaderPinButtonHTML, setupHeaderPin, getNoDataHTML } from '../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from '../../types';
import { format } from 'date-fns';

export class TraderverseMarketEconomicCalendar {
  private container: HTMLElement;
  private config: WidgetConfig;
  private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
  private updateInterval: number | null = null;
  private shadowRoot: ShadowRoot;
  private currentData: any = null;
  private widgetResizeObserver: ResizeObserver | null = null;
  private lastFetchedAt: Date | null = null;
  private startDate: string = '';
  private endDate: string = '';
  private currentRange: string = 'Next Week';
  private selectedCountry: string = 'US';
  private selectedImpacts: Set<string> = new Set();
  private datePickerInstance: flatpickr.Instance | null = null;

  constructor(config: WidgetConfig) {
    this.config = { ...this.getDefaultConfig(), ...config };
    this.config.theme = normalizeWidgetTheme(this.config.theme);
    this.container = this.resolveContainer(config.container);

    if ((this.container as any).shadowRoot) {
      this.shadowRoot = (this.container as any).shadowRoot;
    } else {
      this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
    }
    this.init();

    this.widgetResizeObserver = new ResizeObserver(() => {
      this.updateDisplay();
    });
    setTimeout(() => {
      const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
      if (widget) this.widgetResizeObserver?.observe(widget);
    }, 0);
  }

  private setDateRange(range: string): void {
    const today = new Date();
    let start = new Date();

    switch (range) {
      case 'Current Week':
        start.setDate(today.getDate() - today.getDay());
        break;
      case 'Next Week':
        start.setDate(today.getDate() + (7 - today.getDay()));
        break;
      case 'Last 2 Weeks':
        start.setDate(today.getDate() - 14);
        break;
      case 'Last 3 Months':
        start.setMonth(today.getMonth() - 3);
        break;
      default:
        start.setDate(today.getDate() - 5);
        break;
    }

    this.startDate = start.toISOString().split('T')[0];
    this.endDate = today.toISOString().split('T')[0];
  }

  private fetchSymbols(): Array<{ value: string; label: string }> {
    return [
      { label: 'AAPL', value: 'AAPL' }, { label: 'MSFT', value: 'MSFT' },
      { label: 'GOOGL', value: 'GOOGL' }, { label: 'AMZN', value: 'AMZN' },
      { label: 'NVDA', value: 'NVDA' }, { label: 'META', value: 'META' },
      { label: 'TSLA', value: 'TSLA' }, { label: 'AVGO', value: 'AVGO' },
      { label: 'PEP', value: 'PEP' }, { label: 'COST', value: 'COST' }
    ];
  }

  private getDefaultConfig(): Partial<WidgetConfig> {
    const symbols = this.fetchSymbols();
    return {
      headerPinned: true,
      symbol: symbols[0].value,
      theme: 'default-dark',
      width: '100%',
      height: '400px',
      autosize: true
    };
  }

  private resolveContainer(container: string | HTMLElement): HTMLElement {
    if (typeof container === 'string') {
      const el = document.getElementById(container) || document.querySelector(container);
      if (!el) throw new Error(`Container not found: ${container}`);
      return el as HTMLElement;
    }
    return container;
  }



  private async init(): Promise<void> {
    if (!this.config.symbol) this.config.symbol = this.fetchSymbols()[0].value;

    const today = new Date();
    const fiveDaysAgo = new Date();
    fiveDaysAgo.setDate(today.getDate() - 5);
    this.startDate = fiveDaysAgo.toISOString().split('T')[0];
    this.endDate = today.toISOString().split('T')[0];

    this.createWidget();
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    if (widget) widget.classList.add('loading');
    await this.injectChartLibrary();
    this.attachStyles();
    await this.setupChart();
    if (widget) widget.classList.remove('loading');
    this.emit('ready', {});
    this.config.onReady?.();
    this.setDateRange(this.currentRange);
    cancelSelectEvent(this.shadowRoot);
  }

  private createWidget(): void {
    let themeClass = 'defaultDarkMode';
  let dataTheme = 'default-dark';
  
  if (this.config.theme === 'white') {
    themeClass = 'whiteMode';
    dataTheme = 'white';
  } else if (this.config.theme === 'black') {
    themeClass = 'blackMode';
    dataTheme = 'black';
  } else if (this.config.theme === 'default-lite') {
  themeClass = 'defaultLightMode';
  dataTheme = 'default-lite';
}
    const widgetHtml = `
      <div style="width: ${typeof this.config.width == "number" ? this.config.width+ 'px' : this.config.width  };height: ${typeof this.config.height == "number" ? this.config.height+ 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
        <div class="headTop">
          <h4 class="title market">${this.getTitleIconSvg()} Economic Calendar</h4>
          <div class="actionCol">${getHeaderPinButtonHTML(this.config)}<tooltip-wrapper id="lastUpdatedTooltip"><svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M0.625 6.625C0.625 7.41293 0.780195 8.19315 1.08172 8.9211C1.38325 9.64905 1.82521 10.3105 2.38236 10.8676C2.93951 11.4248 3.60095 11.8667 4.3289 12.1683C5.05685 12.4698 5.83707 12.625 6.625 12.625C7.41293 12.625 8.19315 12.4698 8.9211 12.1683C9.64905 11.8667 10.3105 11.4248 10.8676 10.8676C11.4248 10.3105 11.8667 9.64905 12.1683 8.9211C12.4698 8.19315 12.625 7.41293 12.625 6.625C12.625 5.0337 11.9929 3.50758 10.8676 2.38236C9.74242 1.25714 8.2163 0.625 6.625 0.625C5.0337 0.625 3.50758 1.25714 2.38236 2.38236C1.25714 3.50758 0.625 5.0337 0.625 6.625Z" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/> <path d="M6.625 3.2915V6.62484L8.625 8.62484" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/> </svg><tooltip><span>Last Updated</span><strong>N/A</strong></tooltip></tooltip-wrapper><div class="item">
                <button class="filters-toggle" type="button"> <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M12.1794 0.950592H1.72077C1.57285 0.950469 1.42802 0.991908 1.30363 1.06995C1.17925 1.14799 1.08058 1.25932 1.01944 1.39061C0.958302 1.5219 0.937286 1.66759 0.958909 1.81023C0.980533 1.95286 1.04388 2.0864 1.14136 2.19484L5.22108 6.73834C5.34378 6.8752 5.41132 7.05084 5.41114 7.23259V10.8881C5.41114 10.9463 5.42504 11.0037 5.45175 11.0558C5.47846 11.1079 5.51725 11.1532 5.56503 11.1881L7.87343 12.8756C7.93059 12.9174 7.99856 12.9428 8.06972 12.9491C8.14088 12.9553 8.21242 12.9421 8.27633 12.911C8.34023 12.8799 8.39398 12.832 8.43154 12.7727C8.46911 12.7135 8.489 12.6452 8.489 12.5756V7.23259C8.48882 7.05084 8.55636 6.8752 8.67906 6.73834L12.7588 2.19409C13.1943 1.70959 12.8403 0.950592 12.1794 0.950592Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/> </svg> </button>
              </div></div>
        </div>

        <div class="inBody">
          <div class="optionsRow">
            <div class="item date-range">
              <select class="range-select">
                <option>Current Week</option>
                <option selected>Next Week</option>
                <option>Last 2 Weeks</option>
                <option>Last 3 Months</option>
              </select>
            </div>
            <span></span>

            <div class="filtersBox sideBar hidden">
              <div class="item date-picker">
                <input type="text" class="date-input" />
              </div>
              <div class="item">
                <select class="country-select">
                  <option selected>All Countries</option>
                </select>
              </div>
              <div class="item checkBoxes checkBoxes02">
                <div class="checkBoxField">
                  <input class="impact-check" value="High" type="checkbox" />
                  <label>High <i>✓</i></label>
                </div>
                <div class="checkBoxField">
                  <input class="impact-check" value="Medium" type="checkbox" />
                  <label>Medium <i>✓</i></label>
                </div>
                <div class="checkBoxField">
                  <input class="impact-check" value="Low" type="checkbox" />
                  <label>Low <i>✓</i></label>
                </div>
              </div>
            </div>
          </div>
          <div class="chartDv" id="eventsContainer" style="height: 100%; padding: 0; margin: 0;"></div>
        </div>
      </div>
    `;

    this.shadowRoot.innerHTML = widgetHtml;
    setupHeaderPin(this.shadowRoot, this.config);

    const rangeSelect = this.shadowRoot.querySelector('.range-select') as HTMLSelectElement;
    const countrySelect = this.shadowRoot.querySelector('.country-select') as HTMLSelectElement;
    const dateInput = this.shadowRoot.querySelector('.date-input') as HTMLInputElement;

    const filterBtn = this.shadowRoot.querySelector('.filters-toggle') as HTMLButtonElement;
      const filtersBox = this.shadowRoot.querySelector('.filtersBox') as HTMLElement;
      if (filterBtn && filtersBox) {
        filterBtn.addEventListener('click', (ev: MouseEvent) => {
          ev.stopPropagation();
          filtersBox.classList.toggle('hidden');
        });
        filtersBox.addEventListener('click', (ev: MouseEvent) => ev.stopPropagation());
        filtersBox.addEventListener('pointerdown', (ev: PointerEvent) => ev.stopPropagation());
      }
    if (rangeSelect) {
      rangeSelect.addEventListener('change', () => {
        this.currentRange = rangeSelect.value;
        this.setDateRange(this.currentRange);
        this.setupChart();
        this.initDatePicker();
      });
    }

    if (countrySelect) {
      countrySelect.addEventListener('change', () => {
        this.selectedCountry = countrySelect.value;
        this.filterByCountry(this.selectedCountry);
      });
    }

    const impactChecks = Array.from(this.shadowRoot.querySelectorAll('.impact-check')) as HTMLInputElement[];

    if (impactChecks && impactChecks.length) {
      impactChecks.forEach((chk) => {
        chk.addEventListener('change', () => {
          const v = (chk.value || '').toLowerCase();
          if (chk.checked) this.selectedImpacts.add(v);
          else this.selectedImpacts.delete(v);
          // Re-run filtering when impacts change
          this.filterByCountry(this.selectedCountry);
        });
      });
    }

    setTimeout(() => this.initDatePicker(), 0);
  }

  private initDatePicker(): void {
    try {
      const input = this.shadowRoot.querySelector('.date-input') as HTMLInputElement;
      if (!input) return;

      if (this.datePickerInstance && this.datePickerInstance.destroy) {
        this.datePickerInstance.destroy();
        this.datePickerInstance = null;
      }

      this.datePickerInstance = initFlatpickrInShadow(input, this.shadowRoot, {
        mode: 'range',
        dateFormat: 'Y-m-d',
        defaultDate: [this.startDate || new Date(), this.endDate || new Date()],
        onChange: (selectedDates: Date[]) => {
          if (selectedDates.length === 1) {
            const s = format(selectedDates[0], 'yyyy-MM-dd');
            input.value = s;
          } else if (selectedDates.length === 2) {
            const [start, end] = selectedDates;
            input.value = `${format(start, 'yyyy-MM-dd')} to ${format(end, 'yyyy-MM-dd')}`;
          }
        },
        onClose: (selectedDates: Date[]) => {
          if (selectedDates.length === 2) {
            const [start, end] = selectedDates;
            this.startDate = format(start, 'yyyy-MM-dd');
            this.endDate = format(end, 'yyyy-MM-dd');
            input.value = `${this.startDate} to ${this.endDate}`;
            this.setupChart();
          } else if (selectedDates.length === 1) {
            const s = format(selectedDates[0], 'yyyy-MM-dd');
            this.startDate = s;
            this.endDate = s;
            input.value = s;
            this.setupChart();
          }
        },
      });
    } catch (err) {
      console.warn('Date picker init failed:', err);
    }
  }

  private async attachStyles(): Promise<void> {
    try {
      appendFlatpickrBaseStyles(this.shadowRoot);
      const styleEl = await getCSSStyles();
      this.shadowRoot.appendChild(styleEl);
    } catch (err) {
      console.error('Failed to load CSS file:', err);
    }
  }

  private async injectChartLibrary(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      if ((window as any).echarts) return resolve(true);
      if (this.shadowRoot.querySelector('script[data-chart-lib]')) {
        const script = this.shadowRoot.querySelector('script[data-chart-lib]') as HTMLScriptElement;
        script.onload = () => resolve(true);
      } else {
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
        script.async = true;
        script.setAttribute('data-chart-lib', 'true');
        this.shadowRoot.appendChild(script);
        script.onload = () => resolve(true);
      }
    });
  }

  private async setupChart(): Promise<void> {
    try {
      const payload = {
        start_date: this.startDate,
        end_date: this.endDate
      };

      const response = await apiGet({
        method: "POST",
        url: `${APIURL}/v1/discover/economic_events`,
        body: payload,
      });

      if (!response.ok) throw new Error("Failed to fetch data");
      this.currentData = await response.json();
      const fromApi = (this.currentData as any)?.last_updated_at ?? (this.currentData as any)?.data?.[0]?.last_updated_at;
      this.lastFetchedAt = fromApi ? new Date(fromApi) : new Date();

      this.populateCountryDropdown();
      this.updateDisplay();
      this.emit('dataUpdate', this.currentData);
    } catch (err) {
      console.error("Error fetching chart data:", err);
    }
  }

  private populateCountryDropdown(): void {
    const countrySelect = this.shadowRoot.querySelector('.country-select') as HTMLSelectElement;
    if (!countrySelect || !this.currentData?.data) return;

    // Full static country list (shortened for readability)
    const CountryList = [
      { name: 'Afghanistan', code: 'AF' },
      { name: 'Åland Islands', code: 'AX' },
      { name: 'Albania', code: 'AL' },
      { name: 'Algeria', code: 'DZ' },
      { name: 'American Samoa', code: 'AS' },
      { name: 'AndorrA', code: 'AD' },
      { name: 'Angola', code: 'AO' },
      { name: 'Anguilla', code: 'AI' },
      { name: 'Antarctica', code: 'AQ' },
      { name: 'Antigua and Barbuda', code: 'AG' },
      { name: 'Argentina', code: 'AR' },
      { name: 'Armenia', code: 'AM' },
      { name: 'Aruba', code: 'AW' },
      { name: 'Australia', code: 'AU' },
      { name: 'Austria', code: 'AT' },
      { name: 'Azerbaijan', code: 'AZ' },
      { name: 'Bahamas', code: 'BS' },
      { name: 'Bahrain', code: 'BH' },
      { name: 'Bangladesh', code: 'BD' },
      { name: 'Barbados', code: 'BB' },
      { name: 'Belarus', code: 'BY' },
      { name: 'Belgium', code: 'BE' },
      { name: 'Belize', code: 'BZ' },
      { name: 'Benin', code: 'BJ' },
      { name: 'Bermuda', code: 'BM' },
      { name: 'Bhutan', code: 'BT' },
      { name: 'Bolivia', code: 'BO' },
      { name: 'Bosnia and Herzegovina', code: 'BA' },
      { name: 'Botswana', code: 'BW' },
      { name: 'Bouvet Island', code: 'BV' },
      { name: 'Brazil', code: 'BR' },
      { name: 'British Indian Ocean Territory', code: 'IO' },
      { name: 'Brunei Darussalam', code: 'BN' },
      { name: 'Bulgaria', code: 'BG' },
      { name: 'Burkina Faso', code: 'BF' },
      { name: 'Burundi', code: 'BI' },
      { name: 'Cambodia', code: 'KH' },
      { name: 'Cameroon', code: 'CM' },
      { name: 'Canada', code: 'CA' },
      { name: 'Cape Verde', code: 'CV' },
      { name: 'Cayman Islands', code: 'KY' },
      { name: 'Central African Republic', code: 'CF' },
      { name: 'Chad', code: 'TD' },
      { name: 'Chile', code: 'CL' },
      { name: 'China', code: 'CN' },
      { name: 'Christmas Island', code: 'CX' },
      { name: 'Cocos (Keeling) Islands', code: 'CC' },
      { name: 'Colombia', code: 'CO' },
      { name: 'Comoros', code: 'KM' },
      { name: 'Congo', code: 'CG' },
      { name: 'Congo, The Democratic Republic of the', code: 'CD' },
      { name: 'Cook Islands', code: 'CK' },
      { name: 'Costa Rica', code: 'CR' },
      { name: 'Cote D\'Ivoire', code: 'CI' },
      { name: 'Croatia', code: 'HR' },
      { name: 'Cuba', code: 'CU' },
      { name: 'Cyprus', code: 'CY' },
      { name: 'Czech Republic', code: 'CZ' },
      { name: 'Denmark', code: 'DK' },
      { name: 'Djibouti', code: 'DJ' },
      { name: 'Dominica', code: 'DM' },
      { name: 'Dominican Republic', code: 'DO' },
      { name: 'Ecuador', code: 'EC' },
      { name: 'Egypt', code: 'EG' },
      { name: 'El Salvador', code: 'SV' },
      { name: 'Equatorial Guinea', code: 'GQ' },
      { name: 'Eritrea', code: 'ER' },
      { name: 'Estonia', code: 'EE' },
      { name: 'Ethiopia', code: 'ET' },
      { name: 'Falkland Islands (Malvinas)', code: 'FK' },
      { name: 'Faroe Islands', code: 'FO' },
      { name: 'Fiji', code: 'FJ' },
      { name: 'Finland', code: 'FI' },
      { name: 'France', code: 'FR' },
      { name: 'French Guiana', code: 'GF' },
      { name: 'French Polynesia', code: 'PF' },
      { name: 'French Southern Territories', code: 'TF' },
      { name: 'Gabon', code: 'GA' },
      { name: 'Gambia', code: 'GM' },
      { name: 'Georgia', code: 'GE' },
      { name: 'Germany', code: 'DE' },
      { name: 'Ghana', code: 'GH' },
      { name: 'Gibraltar', code: 'GI' },
      { name: 'Greece', code: 'GR' },
      { name: 'Greenland', code: 'GL' },
      { name: 'Grenada', code: 'GD' },
      { name: 'Guadeloupe', code: 'GP' },
      { name: 'Guam', code: 'GU' },
      { name: 'Guatemala', code: 'GT' },
      { name: 'Guernsey', code: 'GG' },
      { name: 'Guinea', code: 'GN' },
      { name: 'Guinea-Bissau', code: 'GW' },
      { name: 'Guyana', code: 'GY' },
      { name: 'Haiti', code: 'HT' },
      { name: 'Heard Island and Mcdonald Islands', code: 'HM' },
      { name: 'Holy See (Vatican City State)', code: 'VA' },
      { name: 'Honduras', code: 'HN' },
      { name: 'Hong Kong', code: 'HK' },
      { name: 'Hungary', code: 'HU' },
      { name: 'Iceland', code: 'IS' },
      { name: 'India', code: 'IN' },
      { name: 'Indonesia', code: 'ID' },
      { name: 'Iran, Islamic Republic Of', code: 'IR' },
      { name: 'Iraq', code: 'IQ' },
      { name: 'Ireland', code: 'IE' },
      { name: 'Isle of Man', code: 'IM' },
      { name: 'Israel', code: 'IL' },
      { name: 'Italy', code: 'IT' },
      { name: 'Jamaica', code: 'JM' },
      { name: 'Japan', code: 'JP' },
      { name: 'Jersey', code: 'JE' },
      { name: 'Jordan', code: 'JO' },
      { name: 'Kazakhstan', code: 'KZ' },
      { name: 'Kenya', code: 'KE' },
      { name: 'Kiribati', code: 'KI' },
      { name: 'Korea, Democratic People\'S Republic of', code: 'KP' },
      { name: 'Korea, Republic of', code: 'KR' },
      { name: 'Kuwait', code: 'KW' },
      { name: 'Kyrgyzstan', code: 'KG' },
      { name: 'Lao People\'S Democratic Republic', code: 'LA' },
      { name: 'Latvia', code: 'LV' },
      { name: 'Lebanon', code: 'LB' },
      { name: 'Lesotho', code: 'LS' },
      { name: 'Liberia', code: 'LR' },
      { name: 'Libyan Arab Jamahiriya', code: 'LY' },
      { name: 'Liechtenstein', code: 'LI' },
      { name: 'Lithuania', code: 'LT' },
      { name: 'Luxembourg', code: 'LU' },
      { name: 'Macao', code: 'MO' },
      { name: 'Macedonia, The Former Yugoslav Republic of', code: 'MK' },
      { name: 'Madagascar', code: 'MG' },
      { name: 'Malawi', code: 'MW' },
      { name: 'Malaysia', code: 'MY' },
      { name: 'Maldives', code: 'MV' },
      { name: 'Mali', code: 'ML' },
      { name: 'Malta', code: 'MT' },
      { name: 'Marshall Islands', code: 'MH' },
      { name: 'Martinique', code: 'MQ' },
      { name: 'Mauritania', code: 'MR' },
      { name: 'Mauritius', code: 'MU' },
      { name: 'Mayotte', code: 'YT' },
      { name: 'Mexico', code: 'MX' },
      { name: 'Micronesia, Federated States of', code: 'FM' },
      { name: 'Moldova, Republic of', code: 'MD' },
      { name: 'Monaco', code: 'MC' },
      { name: 'Mongolia', code: 'MN' },
      { name: 'Montserrat', code: 'MS' },
      { name: 'Morocco', code: 'MA' },
      { name: 'Mozambique', code: 'MZ' },
      { name: 'Myanmar', code: 'MM' },
      { name: 'Namibia', code: 'NA' },
      { name: 'Nauru', code: 'NR' },
      { name: 'Nepal', code: 'NP' },
      { name: 'Netherlands', code: 'NL' },
      { name: 'Netherlands Antilles', code: 'AN' },
      { name: 'New Caledonia', code: 'NC' },
      { name: 'New Zealand', code: 'NZ' },
      { name: 'Nicaragua', code: 'NI' },
      { name: 'Niger', code: 'NE' },
      { name: 'Nigeria', code: 'NG' },
      { name: 'Niue', code: 'NU' },
      { name: 'Norfolk Island', code: 'NF' },
      { name: 'Northern Mariana Islands', code: 'MP' },
      { name: 'Norway', code: 'NO' },
      { name: 'Oman', code: 'OM' },
      { name: 'Pakistan', code: 'PK' },
      { name: 'Palau', code: 'PW' },
      { name: 'Palestinian Territory, Occupied', code: 'PS' },
      { name: 'Panama', code: 'PA' },
      { name: 'Papua New Guinea', code: 'PG' },
      { name: 'Paraguay', code: 'PY' },
      { name: 'Peru', code: 'PE' },
      { name: 'Philippines', code: 'PH' },
      { name: 'Pitcairn', code: 'PN' },
      { name: 'Poland', code: 'PL' },
      { name: 'Portugal', code: 'PT' },
      { name: 'Puerto Rico', code: 'PR' },
      { name: 'Qatar', code: 'QA' },
      { name: 'Reunion', code: 'RE' },
      { name: 'Romania', code: 'RO' },
      { name: 'Russian Federation', code: 'RU' },
      { name: 'RWANDA', code: 'RW' },
      { name: 'Saint Helena', code: 'SH' },
      { name: 'Saint Kitts and Nevis', code: 'KN' },
      { name: 'Saint Lucia', code: 'LC' },
      { name: 'Saint Pierre and Miquelon', code: 'PM' },
      { name: 'Saint Vincent and the Grenadines', code: 'VC' },
      { name: 'Samoa', code: 'WS' },
      { name: 'San Marino', code: 'SM' },
      { name: 'Sao Tome and Principe', code: 'ST' },
      { name: 'Saudi Arabia', code: 'SA' },
      { name: 'Senegal', code: 'SN' },
      { name: 'Serbia and Montenegro', code: 'CS' },
      { name: 'Seychelles', code: 'SC' },
      { name: 'Sierra Leone', code: 'SL' },
      { name: 'Singapore', code: 'SG' },
      { name: 'Slovakia', code: 'SK' },
      { name: 'Slovenia', code: 'SI' },
      { name: 'Solomon Islands', code: 'SB' },
      { name: 'Somalia', code: 'SO' },
      { name: 'South Africa', code: 'ZA' },
      { name: 'South Georgia and the South Sandwich Islands', code: 'GS' },
      { name: 'Spain', code: 'ES' },
      { name: 'Sri Lanka', code: 'LK' },
      { name: 'Sudan', code: 'SD' },
      { name: 'Suriname', code: 'SR' },
      { name: 'Svalbard and Jan Mayen', code: 'SJ' },
      { name: 'Swaziland', code: 'SZ' },
      { name: 'Sweden', code: 'SE' },
      { name: 'Switzerland', code: 'CH' },
      { name: 'Syrian Arab Republic', code: 'SY' },
      { name: 'Taiwan, Province of China', code: 'TW' },
      { name: 'Tajikistan', code: 'TJ' },
      { name: 'Tanzania, United Republic of', code: 'TZ' },
      { name: 'Thailand', code: 'TH' },
      { name: 'Timor-Leste', code: 'TL' },
      { name: 'Togo', code: 'TG' },
      { name: 'Tokelau', code: 'TK' },
      { name: 'Tonga', code: 'TO' },
      { name: 'Trinidad and Tobago', code: 'TT' },
      { name: 'Tunisia', code: 'TN' },
      { name: 'Turkey', code: 'TR' },
      { name: 'Turkmenistan', code: 'TM' },
      { name: 'Turks and Caicos Islands', code: 'TC' },
      { name: 'Tuvalu', code: 'TV' },
      { name: 'Uganda', code: 'UG' },
      { name: 'Ukraine', code: 'UA' },
      { name: 'United Arab Emirates', code: 'AE' },
      { name: 'United Kingdom', code: 'GB' },
      { name: 'United States', code: 'US' },
      { name: 'United States Minor Outlying Islands', code: 'UM' },
      { name: 'Uruguay', code: 'UY' },
      { name: 'Uzbekistan', code: 'UZ' },
      { name: 'Vanuatu', code: 'VU' },
      { name: 'Venezuela', code: 'VE' },
      { name: 'Viet Nam', code: 'VN' },
      { name: 'Virgin Islands, British', code: 'VG' },
      { name: 'Virgin Islands, U.S.', code: 'VI' },
      { name: 'Wallis and Futuna', code: 'WF' },
      { name: 'Western Sahara', code: 'EH' },
      { name: 'Yemen', code: 'YE' },
      { name: 'Zambia', code: 'ZM' },
      { name: 'Zimbabwe', code: 'ZW' }
    ];

    const apiCountries = Array.from(new Set(this.currentData.data.map((item: any) => item.country)));

    const filteredCountries = CountryList.filter(c => apiCountries.includes(c.code));

    countrySelect.innerHTML = `<option>All Countries</option>`;
    for (const c of filteredCountries) {
      const option = document.createElement('option');
      option.value = c.code;
      option.textContent = c.name;
      if (c.code === this.selectedCountry) option.selected = true;
      countrySelect.appendChild(option);
    }
    this.filterByCountry(this.selectedCountry);



  }


  private filterByCountry(selectedCountry: string): void {
    if (!this.currentData?.data) return;
    let filtered = this.currentData.data;

    if (selectedCountry !== 'All Countries') {
      filtered = filtered.filter((item: any) => item.country === selectedCountry);
    }
    if (this.selectedImpacts && this.selectedImpacts.size > 0) {
      filtered = filtered.filter((item: any) => {
        const impact = (item.impact || '').toString().toLowerCase();
        return this.selectedImpacts.has(impact);
      });
    }

    this.renderFilteredEvents(filtered);
  }


  private renderFilteredEvents(eventsData: any[]): void {
    const container = this.shadowRoot.querySelector('#eventsContainer') as HTMLElement | null;
    const chartDv = this.shadowRoot.querySelector('.chartDv') as HTMLElement | null;

    if (!container || !chartDv) return;

const hasData = Array.isArray(eventsData) && eventsData.length > 0;

if (!hasData) {
  chartDv.removeAttribute('data-testing-id');
  chartDv.innerHTML = getNoDataHTML();
  return;
}

chartDv.setAttribute('data-testing-id', 'WidgetbodyTable');

    let html = `
      <div class="eventsMain">
        <div class="item header">
          <p>Time</p>
          <p>Country</p>
          <p>Actual</p>
          <p>Estimate</p>
          <p>Previous</p>
          <p>Impact</p>
        </div>
    `;

    for (const item of eventsData) {
      html += `
        <div class="item body">
                <p>
        ${new Date(item.date).toLocaleString(undefined, {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        })}
      </p>
          <p><img src="${item.countryFlag}" width="20" height="14" style="vertical-align:middle;" /> ${item.event}</p>
          <p>${item.actual ?? '-'}</p>
          <p>${item.estimate ?? '-'}</p>
          <p>${item.previous ?? '-'}</p>
          <p class="stat ${item.impact?.toLowerCase() ?? ''}"><span>${item.impact ?? '-'}</span></p>
        </div>
      `;
    }

    html += `</div>`;
    container.innerHTML = html;
  }

  private formatLastUpdated(date: Date): string {
    const options: Intl.DateTimeFormatOptions = {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'America/New_York',
    };
    return date.toLocaleString('en-US', options).replace(',', '').replace(/\//g, '-') + ' EST';
  }

  private updateLastUpdatedTooltip(): void {
    const lastUpdated = this.lastFetchedAt ? this.formatLastUpdated(this.lastFetchedAt) : 'N/A';
    const tooltip = this.shadowRoot.querySelector('#lastUpdatedTooltip tooltip strong');
    if (tooltip) tooltip.innerHTML = lastUpdated.replace(' ', '<br>');
  }

  private updateDisplay(): void {
    this.updateLastUpdatedTooltip();
    if (!this.currentData) {
      setTimeout(() => this.updateDisplay(), 100);
      return;
    }
    this.filterByCountry(this.selectedCountry);
  }

  public on(type: WidgetEventType, handler: WidgetEventHandler): void {
    if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
    this.eventHandlers.get(type)!.push(handler);
  }

  public off(type: WidgetEventType, handler: WidgetEventHandler): void {
    const list = this.eventHandlers.get(type);
    if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
  }

  public setSymbol(symbol: string): void {
    this.config.symbol = symbol;
    this.setupChart();
  }

  public async getWidgetImage(): Promise<string> {
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    return await getWidgetDataURI(widget);
  }

  
  public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
    if (!this.shadowRoot) return;
  
    this.config.theme = toggleWidgetTheme(
      this.shadowRoot,
      theme
    ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';
  
    this.updateDisplay();
  }

  public destroy(): void {
    if (this.updateInterval) { clearInterval(this.updateInterval); this.updateInterval = null; }
    if (this.widgetResizeObserver) { this.widgetResizeObserver.disconnect(); this.widgetResizeObserver = null; }
    this.eventHandlers.clear(); this.shadowRoot.innerHTML = '';
  }

  private emit(type: WidgetEventType, data: any): void {
    const handlers = this.eventHandlers.get(type);
    if (handlers) { const evt: WidgetEvent = { type, data }; handlers.forEach(h => h(evt)); }
  }
  private getTitleIconSvg(): string {
    return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> <path d="M9.16252 19.05C8.96252 19.05 8.76252 18.9875 8.58752 18.85C8.17502 18.5375 8.10002 17.95 8.41252 17.5375L11.3875 13.675C11.75 13.2125 12.2625 12.9125 12.85 12.8375C13.425 12.7625 14.0125 12.925 14.475 13.2875L16.7625 15.0875C16.85 15.1625 16.9375 15.1625 17 15.15C17.05 15.15 17.1375 15.125 17.2125 15.025L20.1 11.3C20.4125 10.8875 21.0125 10.8125 21.4125 11.1375C21.825 11.45 21.9 12.0375 21.575 12.45L18.6875 16.175C18.325 16.6375 17.8125 16.9375 17.225 17C16.6375 17.075 16.0625 16.9125 15.6 16.55L13.3125 14.75C13.225 14.675 13.125 14.675 13.075 14.6875C13.025 14.6875 12.9375 14.7125 12.8625 14.8125L9.88752 18.675C9.72502 18.925 9.45002 19.05 9.16252 19.05Z" fill="currentColor"/> </svg>`;
  }

}

declare global { interface Window { TraderverseMarketEconomicCalendar: typeof TraderverseMarketEconomicCalendar; } }
if (typeof window !== 'undefined') { window.TraderverseMarketEconomicCalendar = TraderverseMarketEconomicCalendar; }